import axios from 'axios';
import Cookies from 'js-cookie';
import { BASE_URL } from '../actions';

export const REPORT_SCOPES = {
  all: 'All current records',
  before_association: 'Before joining this firm',
  after_association: 'After joining this firm',
};
export const REPORT_SECTIONS = {
  pain_logs: 'Pain logs', intake: 'Intake answers', documents: 'Documents',
  expenses: 'Expenses', appointments: 'Appointments',
};
export const defaultReportOptions = () => Object.fromEntries(Object.keys(REPORT_SECTIONS).map(key => [key, true]));

export function reportRequest(caseId, search) {
  const query = new URLSearchParams(search);
  const scope = query.get('scope');
  if (!Number.isSafeInteger(Number(caseId)) || Number(caseId) <= 0 || !Object.hasOwn(REPORT_SCOPES, scope)) {
    throw new Error('Choose a case and reporting period before generating a report.');
  }
  const request = { caseId: Number(caseId), scope, options: defaultReportOptions() };
  for (const key of ['from_date', 'to_date']) {
    const value = query.get(key);
    if (value) {
      const date = new Date(`${value}T00:00:00Z`);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value) {
        throw new Error('Choose valid event dates.');
      }
      request[key] = value;
    }
  }
  if (request.from_date && request.to_date && request.from_date > request.to_date) {
    throw new Error('The end date must be on or after the start date.');
  }
  for (const key of Object.keys(REPORT_SECTIONS)) request.options[key] = query.get(key) !== 'false';
  if (query.has('document_ids')) {
    let selection;
    try { selection = JSON.parse(query.get('document_ids')); } catch { throw new Error('Choose a valid document selection.'); }
    if (!Array.isArray(selection) || selection.length > 500 || selection.some(item => !item ||
      !['files', 'folders', 'documents', 'legacy_documents'].includes(item.source_table) ||
      (item.source_table === 'legacy_documents' ? typeof item.id !== 'string' || !item.id || item.id.length > 300 :
        !Number.isSafeInteger(item.id) || item.id <= 0))) throw new Error('Choose a valid document selection.');
    request.document_ids = selection.map(({ source_table, id }) => ({ source_table, id }));
  }
  return request;
}

export function reportUrl(caseId, { scope, from_date, to_date, options, document_ids }) {
  const query = new URLSearchParams({ scope });
  if (from_date) query.set('from_date', from_date);
  if (to_date) query.set('to_date', to_date);
  for (const [key, value] of Object.entries(options || {})) query.set(key, String(value));
  if (document_ids !== undefined) query.set('document_ids', JSON.stringify(document_ids));
  reportRequest(caseId, query.toString());
  return `/client-summary-report/${caseId}?${query.toString()}`;
}

export async function fetchReport(request, signal) {
  try {
    const response = await axios.post(`${BASE_URL}/case/report-data`, request, {
      signal, timeout: 60000, headers: { Authorization: `Bearer ${Cookies.get('token')}` },
    });
    const report = response.data?.report;
    if (!response.data?.ok || !report?.manifest || report.manifest.scope !== request.scope ||
      Number(report.manifest.case_id) !== request.caseId) {
      throw new Error('Invalid report response');
    }
    return report;
  } catch (error) {
    if (signal?.aborted) throw error;
    throw new Error(error.response?.data?.message || 'The report could not be generated. Please try again.');
  }
}
